import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { Client } from '../../../../projects/kiene-core/src/lib/components/client/classes/client';
import { TierlisteInternalImportHandler } from '../../../../projects/kiene-core/src/lib/components/ausstallen/tierliste-internal-import.handler';
import { Tierliste } from '../../../../projects/kiene-core/src/lib/components/gruppe-tier/classes/tierliste';
import { Gruppe } from '../../../../projects/kiene-core/src/lib/components/gruppen/classes/gruppe';
import { ImportOptionenDialogComponent } from '../../../../projects/kiene-core/src/lib/components/gruppen/import-optionen-dialog/import-optionen-dialog.component';
import { getGruppeAuswaehlenDialogShellConfig } from '../../../../projects/kiene-core/src/lib/components/gruppen/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog';
import { API_BASE_URL_SERVICE } from '../../../../projects/kiene-core/src/lib/kiene-core.config';
import { AppService } from '../../../../projects/kiene-core/src/lib/services/app/app.service';
import { KieneBackendApiService } from '../../../../projects/kiene-core/src/lib/services/backend-api/kiene-backend-api.service';
import { MessageService } from '../../../../projects/kiene-core/src/lib/services/message-service/message.service';
import { GruppeAuswaehlenDialogComponent } from '../tierlisten/aktuelle-tierlisten/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog.component';
import { GruppeAuswaehlenDialogInput2 } from '../tierlisten/aktuelle-tierlisten/gruppe-auswaehlen-dialog/gruppe-auswaehlen-dialog';
import { GruppenDashboardImportRefreshService } from './gruppen-dashboard-import-refresh.service';

@Injectable()
export class DashboardTierlisteInternalImportHandler implements TierlisteInternalImportHandler {
    constructor(
        private readonly dialog: MatDialog,
        private readonly api: KieneBackendApiService,
        private readonly messageService: MessageService,
        private readonly appService: AppService,
        private readonly gruppenDashboardImportRefresh: GruppenDashboardImportRefreshService,
        @Inject(API_BASE_URL_SERVICE) private readonly apiBaseUrl: string,
    ) {}

    open(tierliste: Tierliste): Observable<boolean> {
        const kundeId = Number(tierliste?.kunde_id_einstallen || 0);
        if (kundeId <= 0) {
            return of(false);
        }

        const params = new HttpParams().set('kunde_id', String(kundeId));
        return this.api.get(this.apiBaseUrl + 'kunde/read_one_kundenportal.php', params).pipe(
            switchMap((kunde: Client) => {
                if (!kunde) {
                    return of(false);
                }
                return this.openGruppeAuswaehlen(tierliste, kunde);
            }),
            catchError(err => {
                this.messageService.errorMessage('Fehler', err);
                return of(false);
            }),
        );
    }

    private openGruppeAuswaehlen(tierliste: Tierliste, kunde: Client): Observable<boolean> {
        const input = new GruppeAuswaehlenDialogInput2();
        input.client = kunde;
        return this.dialog
            .open<GruppeAuswaehlenDialogComponent, GruppeAuswaehlenDialogInput2, Gruppe>(
                GruppeAuswaehlenDialogComponent,
                { ...getGruppeAuswaehlenDialogShellConfig(), data: input },
            )
            .afterClosed()
            .pipe(
                switchMap(gruppe => {
                    if (!gruppe) {
                        return of(false);
                    }
                    return this.openImportOptionen(tierliste, gruppe);
                }),
            );
    }

    private openImportOptionen(tierliste: Tierliste, gruppe: Gruppe): Observable<boolean> {
        const payload: Tierliste = { ...tierliste };
        payload.gruppe_id_einstallen = gruppe.gruppe_id;
        payload.status_id = 163;

        return this.dialog
            .open(ImportOptionenDialogComponent, {
                maxWidth: '600px',
                data: { tierliste: payload },
            })
            .afterClosed()
            .pipe(
                switchMap(updatedTierliste => {
                    if (!updatedTierliste) {
                        return of(false);
                    }
                    return this.api
                        .post(this.apiBaseUrl + 'tierliste/update_kundenportal.php', updatedTierliste, undefined)
                        .pipe(
                            switchMap(() => this.persistImportedEinstallgewichte(updatedTierliste)),
                            tap(() => {
                                const importedTierlisteId = Number(updatedTierliste?.tierliste_id || tierliste?.tierliste_id || 0);
                                this.gruppenDashboardImportRefresh.notifyImportCompleted(importedTierlisteId);
                            }),
                            map(() => true),
                        );
                }),
                catchError(err => {
                    this.messageService.errorMessage('Fehler!', err);
                    return of(false);
                }),
            );
    }

    private persistImportedEinstallgewichte(tierliste: Tierliste): Observable<void> {
        const records = tierliste?.import_einstallgewichte_records || [];
        const gruppeId = Number(tierliste?.gruppe_id_einstallen);
        if (!records.length || gruppeId <= 0) {
            this.appService.setBlockOnLoading(false);
            this.messageService.infoMessage('Die Tiere wurden erfolgreich der Gruppe hinzugefügt!');
            return of(undefined);
        }

        return this.api
            .post(this.apiBaseUrl + 'gruppe_standort/save_einstallgewichte.php', {
                gs_id: 0,
                gruppe_id: gruppeId,
                tierliste_id: Number(tierliste?.tierliste_id || 0),
                records,
            })
            .pipe(
                map(() => {
                    this.appService.setBlockOnLoading(false);
                    this.messageService.infoMessage('Die Tiere wurden erfolgreich der Gruppe hinzugefügt!');
                }),
                catchError(err => {
                    this.messageService.errorMessage(
                        'Import erfolgreich, aber Einstallgewichte konnten nicht vollstaendig gespeichert werden!',
                        err,
                    );
                    return of(undefined);
                }),
            );
    }
}
